// Configuration Setup (/sales/configurator): the configurator's live catalogue, model by model (its categories
// and items with their prices, photos and assembly fields), and what each item writes on the boat's order file
// (packages/shared domain/configuratorOrder.ts). A confirmed order fills in the boat's Order Sheet row through
// those ties, and its order file and BOM follow the row, so each item shows the order file rows it ticks or
// fills in and the option sections of the model's BOM list (BOM lists page) it brings in. An item with no ties
// of its own goes by the ones suggested from its name and category; Edit sets them by hand.

import { useEffect, useMemo, useState, type HTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CATALOGUE_CATEGORY_KINDS,
  CATALOGUE_INPUT_TYPES,
  ORDER_FILE_TEMPLATE,
  configuredOrderCells,
  modelKey,
  orderCellIsTick,
  orderCellsFor,
  orderCellsText,
  orderFileLabel,
  orderListFolder,
  resolveBom,
  suggestOrderCells,
  type AdminCatalogueCategory,
  type AdminCatalogueItem,
  type AdminCatalogueModel,
  type BomLink,
  type BomList,
  type CatalogueItemInput,
  type OrderCell,
} from '@technohull/shared';
import {
  useAdminCatalogue,
  useDeleteCategory,
  useDiscontinueItem,
  useReorderCatalogue,
  useSaveCategory,
  useSaveItem,
  useSaveModel,
} from '../../features/adminCatalogue.js';
import { bomSeasonFor, useBom, useBomList } from '../../features/bom.js';
import { useDeletePhoto, usePhotos, useUploadPhoto } from '../../features/hooks.js';
import { EMPTY_PHOTOS, PhotoStrip } from '../../components/PhotoStrip.js';
import {
  EmptyState, ErrorState, Fact, FactEnd, FactSentence, FilterToggle, InlineError, LoadingState, Modal, PageHeader, Pill,
  SearchInput, Segmented, SyncLabel, useConfirm,
} from '../../components/ui.js';
import { IconAlertTriangle, IconCamera, IconChevronRight, IconClose, IconPlus } from '../../components/icons.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useSeason } from '../../season/SeasonContext.js';
import { useDateFormatter } from '../../lib/format.js';
import { fold, hasText } from '../../lib/search.js';

const money = (n: number | null) => (n == null ? '' : `€${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const KIND_LABEL: Record<string, string> = { standard: 'Standard', optional: 'Optional', production: 'Production' };
const kindLabel = (k: string) => KIND_LABEL[k] ?? k;
/** an option the customer picks (a standard item comes with the boat) */
const isOption = (cat: { kind: string }, it: { isStandard: boolean }) => cat.kind !== 'standard' && !it.isStandard;

// ── stable field helpers (module-level so inputs keep focus) ─────────────────────
function TextRow({ label, value, onChange, wide, placeholder, disabled, hint }: { label: string; value: string; onChange: (v: string) => void; wide?: boolean; placeholder?: string; disabled?: boolean; hint?: string }) {
  return (
    <label className={wide ? 'cfgs-wide' : undefined}>
      <span className="field-label">{label}</span>
      <input className="field" value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      {hint && <span className="cfgs-hint">{hint}</span>}
    </label>
  );
}
// inputMode: 'decimal' for prices, 'numeric' for counts, so phones open the matching keypad
function NumRow({ label, value, onChange, disabled, inputMode = 'numeric' }: { label: string; value: number | null; onChange: (v: number | null) => void; disabled?: boolean; inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'] }) {
  return (
    <label>
      <span className="field-label">{label}</span>
      <input className="field" type="number" inputMode={inputMode} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    </label>
  );
}
function Check({ label, checked, onChange, disabled, wide }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; wide?: boolean }) {
  return (
    <label className={`check-row cfgs-check${wide ? ' cfgs-wide' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}
const Up = (p: { size?: number }) => <IconChevronRight size={p.size ?? 15} style={{ transform: 'rotate(-90deg)' }} />;
const Down = (p: { size?: number }) => <IconChevronRight size={p.size ?? 15} style={{ transform: 'rotate(90deg)' }} />;
/** a form section's heading: Archivo, sentence case, with an optional note on the right */
const SubHead = ({ children, note }: { children: ReactNode; note?: ReactNode }) => (
  <div className="cfgs-sub"><h4>{children}</h4>{note != null && <span className="cfgs-sub-note">{note}</span>}</div>
);

// ── what an item does on the order file and in the BOM ──────────────────────────
interface BomContext {
  list: BomList;
  links: Readonly<Record<string, BomLink>>;
  std: readonly string[];
  /** the option sections in for a boat with only the standard equipment */
  base: ReadonlySet<string>;
}
/** the option sections of the model's BOM list the ties bring in, over a boat with only its standard equipment */
function bomBrings(ctx: BomContext | null, cells: readonly OrderCell[]): string[] | null {
  if (!ctx) return null;
  if (!cells.length) return [];
  const values = configuredOrderCells([{ cells, standard: false, selected: true, removed: false }], ctx.std, []);
  return resolveBom(ctx.list, values, ctx.links)
    .filter((r) => r.included && r.section.part === 'opt' && !ctx.base.has(r.section.key))
    .map((r) => r.section.title);
}
/** the order file's rows that take a value, grouped under their headings, for a picker */
const ORDER_ROW_GROUPS: ReadonlyArray<{ label: string; rows: Array<{ key: string; label: string }> }> = (() => {
  const out: Array<{ label: string; rows: Array<{ key: string; label: string }> }> = [];
  for (const r of ORDER_FILE_TEMPLATE) {
    if (r.kind === 'band') continue;
    if (r.kind === 'section') { out.push({ label: r.en.replace(/[:\s]+$/, ''), rows: [] }); continue; }
    if (!out.length) out.push({ label: 'Order', rows: [] });
    out[out.length - 1]!.rows.push({ key: r.key, label: orderFileLabel(r.key) });
  }
  return out.filter((g) => g.rows.length);
})();

/** the ties as chips: a ticked row by its name, a text row with what it writes */
function TieChips({ cells, from }: { cells: readonly OrderCell[]; from: 'saved' | 'suggested' }) {
  return (
    <span className="cfgs-ties">
      {cells.map((c) => (
        <span key={c.field} className={`cfgs-tie${from === 'suggested' ? ' sug' : ''}`} title={from === 'suggested' ? 'Suggested from the item’s name and category' : 'Set on this page'}>
          {orderFileLabel(c.field)}{!orderCellIsTick(c.field) && c.value && <b>{c.value}</b>}
        </span>
      ))}
    </span>
  );
}

// ── the item's order file ties, in the item editor ──────────────────────────────
function OrderTiesEditor({ value, suggested, onChange, bom, bomFrom }: {
  /** the saved ties; null goes by the suggestion */
  value: OrderCell[] | null;
  suggested: OrderCell[];
  onChange: (v: OrderCell[] | null) => void;
  /** what the ties bring into the BOM (null: no BOM list for the model) */
  bom: string[] | null;
  bomFrom: string;
}) {
  const manual = value !== null;
  const cells = value ?? suggested;
  const setRow = (i: number, p: Partial<OrderCell>) => onChange(cells.map((c, n) => (n === i ? { ...c, ...p, ...(p.field && orderCellIsTick(p.field) ? { value: '' } : {}) } : c)));
  const used = new Set(cells.map((c) => c.field));
  const free = ORDER_ROW_GROUPS.flatMap((g) => g.rows).find((r) => !used.has(r.key));
  return (
    <div className="cfgs-ties-ed">
      <Segmented
        ariaLabel="Where the ties come from"
        value={manual ? 'manual' : 'auto'}
        onChange={(v) => onChange(v === 'manual' ? [...suggested] : null)}
        options={[{ value: 'auto', label: 'Suggested' }, { value: 'manual', label: 'Set by hand' }]}
      />
      {!manual ? (
        <p className="cfgs-ties-read">
          {suggested.length ? <>From its name and category: <TieChips cells={suggested} from="suggested" /></> : 'Nothing in its name or category matches a row of the order file, so it writes nothing there. Set it by hand if it should.'}
        </p>
      ) : (
        <div className="cfgs-rows" role="group" aria-label="Order file rows">
          {cells.map((c, i) => (
            <div key={i} className="cfgs-row">
              <select className="field" aria-label={`Order file row ${i + 1}`} value={c.field} onChange={(e) => setRow(i, { field: e.target.value })}>
                {ORDER_ROW_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.rows.map((r) => <option key={r.key} value={r.key} disabled={r.key !== c.field && used.has(r.key)}>{r.label}</option>)}
                  </optgroup>
                ))}
              </select>
              {orderCellIsTick(c.field)
                ? <span className="cfgs-row-tick">Ticked</span>
                : <input className="field" aria-label={`What it writes on ${orderFileLabel(c.field)}`} placeholder="What it writes, e.g. 400R V10" value={c.value} onChange={(e) => setRow(i, { value: e.target.value })} />}
              <button type="button" className="btn btn-sm btn-ghost cfgs-row-x" aria-label={`Remove ${orderFileLabel(c.field)}`} onClick={() => onChange(cells.filter((_, n) => n !== i))}><IconClose size={15} /></button>
            </div>
          ))}
          {!cells.length && <p className="cfgs-ties-read">Writes nothing on the order file.</p>}
          {free && cells.length < 12 && (
            <button type="button" className="btn btn-sm cfgs-row-add" onClick={() => onChange([...cells, { field: free.key, value: '' }])}><IconPlus size={14} /> Add a row</button>
          )}
        </div>
      )}
      <p className="cfgs-ties-bom">
        {bom == null ? <>No BOM list for this model yet, so what it brings into the BOM isn’t known.</>
          : bom.length ? <>Brings into the BOM ({bomFrom}): <b>{bom.join(', ')}</b></>
          : <>Brings no option section into the BOM ({bomFrom}).</>}
      </p>
    </div>
  );
}

// ── item editor ─────────────────────────────────────────────────────────────────
function itemToForm(modelId: number, categoryId: number, it?: AdminCatalogueItem): CatalogueItemInput {
  return {
    id: it?.id,
    modelId,
    categoryId: it?.categoryId ?? categoryId,
    code: it?.code ?? '',
    name: it?.name ?? '',
    description: it?.description ?? '',
    isStandard: it?.isStandard ?? false,
    defaultQuantity: it?.defaultQuantity ?? 1,
    inputType: (it?.inputType as CatalogueItemInput['inputType']) ?? 'toggle',
    hasDrawing: it?.hasDrawing ?? false,
    internalPrice: it?.internalPrice ?? null,
    dealerPrice: it?.dealerPrice ?? null,
    customerPrice: it?.customerPrice ?? null,
    active: it?.active ?? true,
    assemblyExportLabel: it?.assemblyExportLabel ?? '',
    seatingRows: it?.seatingRows ?? null,
    seatsPerRow: it?.seatsPerRow ?? null,
    aftConfiguration: it?.aftConfiguration ?? '',
    fridgeQuantity: it?.fridgeQuantity ?? null,
    fridgeCapacityLitres: it?.fridgeCapacityLitres ?? null,
    compatibilityNote: it?.compatibilityNote ?? '',
    structuredApproved: it?.structuredApproved ?? false,
    orderCells: it?.orderCells ?? null,
  };
}

function ItemModal({ initial, categories, canEditAssembly, bom, bomFrom, onClose }: {
  initial: CatalogueItemInput;
  categories: AdminCatalogueCategory[];
  canEditAssembly: boolean;
  bom: BomContext | null;
  bomFrom: string;
  onClose: () => void;
}) {
  const [f, setF] = useState<CatalogueItemInput>(initial);
  const [showAssembly, setShowAssembly] = useState(false); // assembly fields collapsed until the assembly user reveals them
  const save = useSaveItem();
  const set = <K extends keyof CatalogueItemInput>(k: K, v: CatalogueItemInput[K]) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.name.trim().length > 0 && !!f.categoryId;
  const category = categories.find((c) => c.id === f.categoryId);
  const suggested = useMemo(() => suggestOrderCells({ name: f.name, label: f.assemblyExportLabel, category: category?.name ?? '' }), [f.name, f.assemblyExportLabel, category?.name]);
  const ties = f.orderCells ?? suggested;
  // a row picked but left empty writes nothing: it isn't saved
  const cleanTies = f.orderCells?.filter((c) => orderCellIsTick(c.field) || c.value.trim()) ?? null;

  // photos: keyed by the item's stable code (only available once the item has been saved).
  const refKey = f.code ? `item:${f.code}` : '';
  const photos = usePhotos('catalogue');
  const uploadPhoto = useUploadPhoto();
  const deletePhoto = useDeletePhoto();
  const itemPhotos = (photos.data?.items ?? []).filter((p) => p.refKey === refKey);

  return (
    <Modal
      title={f.id ? `Edit ${f.name || 'item'}` : 'New item'}
      dataTitle
      width={760}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate({ ...f, orderCells: cleanTies }, { onSuccess: onClose })}>
            {save.isPending ? 'Saving…' : 'Save item'}
          </button>
        </>
      }
    >
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      <div className="cfgs-form">
        <TextRow label="Name, as Sales and the agreement show it" value={f.name} onChange={(v) => set('name', v)} wide />
        <TextRow label="Description" value={f.description ?? ''} onChange={(v) => set('description', v)} wide />
        <label>
          <span className="field-label">Category</span>
          <select className="field" value={f.categoryId} onChange={(e) => set('categoryId', Number(e.target.value))}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>
          <span className="field-label">Input type</span>
          <select className="field" value={f.inputType} onChange={(e) => set('inputType', e.target.value as CatalogueItemInput['inputType'])}>
            {CATALOGUE_INPUT_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </label>
        <NumRow label="Default quantity" value={f.defaultQuantity ?? 1} onChange={(v) => set('defaultQuantity', v ?? 1)} />
        <div className="cfgs-checks">
          <Check label="Standard item" checked={!!f.isStandard} onChange={(v) => set('isStandard', v)} />
          <Check label="Has a drawing" checked={!!f.hasDrawing} onChange={(v) => set('hasDrawing', v)} />
          <Check label="Active" checked={!!f.active} onChange={(v) => set('active', v)} />
        </div>
      </div>

      <SubHead note={category && !category.discountEligible ? 'Its category gets no dealer discount' : undefined}>Pricing</SubHead>
      <div className="cfgs-form cfgs-form-3">
        <NumRow label="Internal (cost) €" value={f.internalPrice ?? null} onChange={(v) => set('internalPrice', v)} inputMode="decimal" />
        <NumRow label="Dealer €" value={f.dealerPrice ?? null} onChange={(v) => set('dealerPrice', v)} inputMode="decimal" />
        <NumRow label="Customer €" value={f.customerPrice ?? null} onChange={(v) => set('customerPrice', v)} inputMode="decimal" />
      </div>

      <SubHead note={ties.length ? orderCellsText(ties) : 'Nothing'}>On the order file</SubHead>
      <p className="cfgs-lead">What a confirmed order with this item writes on the boat’s Order Sheet row. The order file and the boat’s BOM follow that row.</p>
      <OrderTiesEditor value={f.orderCells ?? null} suggested={suggested} onChange={(v) => set('orderCells', v)} bom={bomBrings(bom, ties)} bomFrom={bomFrom} />

      <SubHead note={refKey ? `${itemPhotos.length} photo${itemPhotos.length === 1 ? '' : 's'}` : undefined}>Photos and diagram</SubHead>
      {refKey ? (
        <PhotoStrip
          photos={itemPhotos.length ? itemPhotos : EMPTY_PHOTOS}
          canWrite
          busy={uploadPhoto.isPending}
          onUpload={(file) => uploadPhoto.mutate({ entity: 'catalogue', refKey, file })}
          onDelete={(id) => deletePhoto.mutate({ id, entity: 'catalogue' })}
          deleteMessage={`Delete this photo from “${f.name || f.code}”? It is removed from the live catalogue and can’t be restored.`}
        />
      ) : (
        <p className="cfgs-lead">Save the item first, then open it again to add photos.</p>
      )}
      {(uploadPhoto.isError || deletePhoto.isError) && (
        <InlineError error={uploadPhoto.error ?? deletePhoto.error} onDismiss={() => { uploadPhoto.reset(); deletePhoto.reset(); }} />
      )}

      {/* assembly-owned fields - only visible to users with `configurator:assembly`, and hidden
          behind a toggle so they stay out of the way for the usual sales-facing edits. */}
      {canEditAssembly && (
        <>
          <button type="button" className="cfgs-disclose" aria-expanded={showAssembly} onClick={() => setShowAssembly((s) => !s)}>
            <IconChevronRight size={15} style={{ transform: showAssembly ? 'rotate(90deg)' : undefined }} />
            Assembly fields
            <span className="cfgs-sub-note">Export label, helm and seating</span>
            <Pill kind="info">Assembly</Pill>
          </button>
          {showAssembly && (
            <div className="cfgs-assembly">
              <TextRow label="Assembly export label" hint="Blank uses the name. It is also what the order file gets when the item writes words." value={f.assemblyExportLabel ?? ''} onChange={(v) => set('assemblyExportLabel', v)} wide />
              <SubHead note="Leave blank for items that aren’t a helm">Helm and seating</SubHead>
              <div className="cfgs-form">
                <NumRow label="Seating rows" value={f.seatingRows ?? null} onChange={(v) => set('seatingRows', v)} />
                <NumRow label="Seats per row" value={f.seatsPerRow ?? null} onChange={(v) => set('seatsPerRow', v)} />
                <TextRow label="Aft configuration" value={f.aftConfiguration ?? ''} onChange={(v) => set('aftConfiguration', v)} placeholder="e.g. Back-to-back / wet bar" />
                <div className="cfgs-spacer" />
                <NumRow label="Fridges" value={f.fridgeQuantity ?? null} onChange={(v) => set('fridgeQuantity', v)} />
                <NumRow label="Fridge capacity (L)" value={f.fridgeCapacityLitres ?? null} onChange={(v) => set('fridgeCapacityLitres', v)} />
                <TextRow label="Compatibility note" value={f.compatibilityNote ?? ''} onChange={(v) => set('compatibilityNote', v)} wide />
                <Check label="Use these fields in the Assembly export" checked={!!f.structuredApproved} onChange={(v) => set('structuredApproved', v)} wide />
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ── category editor ───────────────────────────────────────────────────────────
function CategoryModal({ modelId, cat, onClose }: { modelId: number; cat: AdminCatalogueCategory | null; onClose: () => void }) {
  const [name, setName] = useState(cat?.name ?? '');
  const [kind, setKind] = useState(cat?.kind ?? 'standard');
  const [discountEligible, setDiscountEligible] = useState(cat?.discountEligible ?? true);
  const save = useSaveCategory();
  return (
    <Modal
      title={cat ? `Edit ${cat.name}` : 'New category'}
      dataTitle
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate({ id: cat?.id, modelId, name, kind: kind as 'standard' | 'optional' | 'production', discountEligible }, { onSuccess: onClose })}>
            {save.isPending ? 'Saving…' : 'Save category'}
          </button>
        </>
      }
    >
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      <div className="cfgs-form cfgs-form-1">
        <label>
          <span className="field-label">Name</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          <span className="field-label">Kind</span>
          <select className="field" value={kind} onChange={(e) => setKind(e.target.value)}>
            {CATALOGUE_CATEGORY_KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
          </select>
        </label>
        <div>
          <Check label="Gets the dealer discount" checked={discountEligible} onChange={setDiscountEligible} />
          <p className="cfgs-lead">Untick for bespoke features and transport or services: they are billed at full price.</p>
        </div>
      </div>
    </Modal>
  );
}

// ── models editor ───────────────────────────────────────────────────────────────
function ModelsModal({ models, onClose }: { models: AdminCatalogueModel[]; onClose: () => void }) {
  const save = useSaveModel();
  const [draft, setDraft] = useState<{ code: string; name: string; year: string }>({ code: '', name: '', year: '' });
  return (
    <Modal title="Models" width={560} onClose={onClose} footer={<button className="btn btn-ghost" onClick={onClose}>Close</button>}>
      {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr><th>Code</th><th>Name</th><th>Year</th><th>Active</th></tr></thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.id}>
                <td className="cfgs-code">{m.code}</td>
                <td>{m.name}</td>
                <td>{m.year ?? ''}</td>
                <td>
                  {/* the label is the 44px touch target around the box */}
                  <label className="check-row">
                    <input type="checkbox" aria-label={`${m.name} active`} checked={m.active} disabled={save.isPending} onChange={(e) => save.mutate({ id: m.id, code: m.code, name: m.name, year: m.year, active: e.target.checked })} />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SubHead>Add a model</SubHead>
      <div className="cfgm-addrow">
        <input className="field" aria-label="Model code" placeholder="Code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
        <input className="field cfgm-addrow-name" aria-label="Model name" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <input className="field" aria-label="Model year" placeholder="Year" inputMode="numeric" value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value })} />
        <button className="btn btn-sm btn-primary" disabled={!draft.code.trim() || !draft.name.trim() || save.isPending}
          onClick={() => save.mutate({ code: draft.code, name: draft.name, year: draft.year ? Number(draft.year) : null, active: true }, { onSuccess: () => setDraft({ code: '', name: '', year: '' }) })}>Add model</button>
      </div>
    </Modal>
  );
}

// ── entry gate ──────────────────────────────────────────────────────────────────
// shown once per browser session on entry
const DISCLAIMER_ACK_KEY = 'cfg-mgmt-disclaimer-ack-v1';

function EntryDisclaimer({ onAck }: { onAck: () => void }) {
  return (
    <div className="modal-bg" style={{ zIndex: 60 }}>
      <div className="modal cfgs-gate" role="alertdialog" aria-modal="true" aria-labelledby="cfgs-gate-t" aria-describedby="cfgs-gate-b" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <h3 id="cfgs-gate-t"><span className="cfgs-gate-ic" aria-hidden="true"><IconAlertTriangle size={18} /></span>You are editing the live catalogue</h3>
        </div>
        <div className="modal-bd" id="cfgs-gate-b">
          <p>This page controls the real product catalogue for the configurator. There is no draft mode and no separate staging step.</p>
          <p>
            Every change you make here (models, categories, item names, prices, discount eligibility, photos and what
            an item writes on the order file) takes effect immediately for all new sales orders and for every customer
            agreement generated from this point on. Existing confirmed orders keep their frozen snapshot.
          </p>
          <p>
            Edit prices and options only if you are authorised to do so. If you are not sure, please contact an
            administrator. Assembly export fields are owned and edited by the Assembly team.
          </p>
        </div>
        <div className="modal-ft">
          <button className="btn btn-primary" autoFocus onClick={onAck}>I understand, continue</button>
        </div>
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function ConfiguratorManagement() {
  const { can } = useAuth();
  const canEditAssembly = can('configurator:assembly');
  const canProduction = can('production:read');
  const { activeSeason } = useSeason();
  const df = useDateFormatter();
  const [ackDisclaimer, setAckDisclaimer] = useState(() => sessionStorage.getItem(DISCLAIMER_ACK_KEY) === '1');
  const ackAndStore = () => { sessionStorage.setItem(DISCLAIMER_ACK_KEY, '1'); setAckDisclaimer(true); };

  const [modelId, setModelId] = useState<number | null>(null);
  const { data, isLoading, isFetching, error, refetch } = useAdminCatalogue(modelId);
  const reorder = useReorderCatalogue();
  const delCat = useDeleteCategory();
  const discontinue = useDiscontinueItem();
  const photos = usePhotos('catalogue');
  const uploadPhoto = useUploadPhoto();
  const deletePhoto = useDeletePhoto();
  const [confirmNode, confirm] = useConfirm();

  const [itemEdit, setItemEdit] = useState<CatalogueItemInput | null>(null);
  const [catEdit, setCatEdit] = useState<AdminCatalogueCategory | null | undefined>(undefined); // undefined=closed, null=new
  const [modelsOpen, setModelsOpen] = useState(false);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [onlyUntied, setOnlyUntied] = useState(false);

  useEffect(() => {
    if (data && modelId == null) setModelId(data.modelId);
  }, [data, modelId]);

  const categories = useMemo(() => data?.categories ?? [], [data]);
  const currentModelId = modelId ?? data?.modelId ?? null;
  const currentModel = (data?.models ?? []).find((m) => m.id === currentModelId) ?? null;
  // the model's photo carousel shown in the configurator (ref key `gallery:<code>`).
  const galleryRef = currentModel ? `gallery:${currentModel.code}` : '';
  const galleryPhotos = (photos.data?.items ?? []).filter((p) => p.refKey === galleryRef);

  // the model's BOM list (the season's, else the latest before it), its rules and its standard equipment
  const bomModel = currentModel ? modelKey(currentModel.name) : '';
  const bomQ = useBom(Boolean(bomModel));
  const bomSeason = bomQ.data ? bomSeasonFor(bomQ.data.lists, bomModel, activeSeason) : '';
  const bomMeta = bomQ.data?.lists.find((l) => l.model === bomModel && l.season === bomSeason);
  const listQ = useBomList(bomSeason, bomModel, Boolean(bomSeason));
  const bomCtx = useMemo<BomContext | null>(() => {
    if (!listQ.data || !bomQ.data) return null;
    const links = bomQ.data.config.links[bomModel] ?? {};
    const std = bomQ.data.config.std[bomModel] ?? [];
    const base = new Set(resolveBom(listQ.data, configuredOrderCells([], std, []), links).filter((r) => r.included).map((r) => r.section.key));
    return { list: listQ.data, links, std, base };
  }, [listQ.data, bomQ.data, bomModel]);
  const bomFrom = bomMeta ? `${bomSeason} list` : '';

  // each item's ties (saved, else suggested) and the option sections they bring into the BOM
  const linkInfo = useMemo(() => {
    const m = new Map<number, { cells: OrderCell[]; from: 'saved' | 'suggested'; bom: string[] | null }>();
    for (const c of categories) {
      for (const it of c.items) {
        const t = orderCellsFor({ name: it.name, label: it.assemblyExportLabel, category: c.name, orderCells: it.orderCells });
        m.set(it.id, { ...t, bom: isOption(c, it) ? bomBrings(bomCtx, t.cells) : null });
      }
    }
    return m;
  }, [categories, bomCtx]);

  // discontinued (inactive) items stay in the catalogue so existing orders keep resolving, but they
  // clutter the editor — hide them by default (e.g. engine variants the pricelist superseded), with
  // a toggle to reveal them.
  const stats = useMemo(() => {
    let items = 0, inactive = 0, options = 0, untied = 0, bringBom = 0;
    for (const c of categories) {
      for (const it of c.items) {
        if (!it.active) { inactive++; continue; }
        items++;
        if (!isOption(c, it)) continue;
        options++;
        const l = linkInfo.get(it.id);
        if (!l?.cells.length) untied++;
        if (l?.bom?.length) bringBom++;
      }
    }
    return { items, inactive, options, untied, bringBom };
  }, [categories, linkInfo]);

  const filtered = useMemo(() => {
    const query = fold(q.trim());
    return categories
      .map((c) => ({
        ...c,
        items: c.items.filter((it) => {
          if (!showInactive && !it.active) return false;
          if (onlyUntied && (!isOption(c, it) || (linkInfo.get(it.id)?.cells.length ?? 0) > 0)) return false;
          const l = linkInfo.get(it.id);
          return hasText(query, `${it.name} ${it.code} ${it.assemblyExportLabel ?? ''} ${l ? orderCellsText(l.cells) : ''} ${l?.bom?.join(' ') ?? ''}`);
        }),
      }))
      .filter((c) => (!query && !onlyUntied) || c.items.length > 0 || (!onlyUntied && hasText(query, c.name)));
  }, [categories, q, showInactive, onlyUntied, linkInfo]);
  const narrowed = Boolean(q.trim()) || onlyUntied;

  const moveCategory = (idx: number, dir: -1 | 1) => {
    const ids = categories.map((c) => c.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    reorder.mutate({ type: 'category', ids });
  };
  const moveItem = (cat: AdminCatalogueCategory, idx: number, dir: -1 | 1) => {
    const ids = cat.items.map((it) => it.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
    reorder.mutate({ type: 'item', ids });
  };
  const jump = (id: number) => document.getElementById(`cfgs-cat-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (!ackDisclaimer) return <EntryDisclaimer onAck={ackAndStore} />;

  const reordering = reorder.isPending || isFetching; // a swap computed from a stale order would conflict
  const folder = bomModel ? orderListFolder(activeSeason, bomModel).replace(/^order-lists\//, '').replace(/\/$/, '').split('/').join(' › ') : '';

  return (
    <>
      <PageHeader
        title="Configuration Setup"
        meta={currentModel ? <>{currentModel.name}{currentModel.year ? ` · ${currentModel.year}` : ''} · the live catalogue</> : 'The live catalogue'}
      >
        <label className="fsel">
          <span className="fsel-lbl">Model</span>
          <select className="fsel-in" aria-label="Model" value={currentModelId ?? ''} onChange={(e) => setModelId(Number(e.target.value))}>
            {(data?.models ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}{m.active ? '' : ' (inactive)'}</option>)}
          </select>
        </label>
        <button className="btn btn-sm" onClick={() => setModelsOpen(true)}>Models</button>
        {currentModelId && <button className="btn btn-sm" onClick={() => setCatEdit(null)}><IconPlus size={14} /> Category</button>}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} idle="Refresh" busyText="Refreshing…" /></button>
      </PageHeader>

      {isLoading && <LoadingState label="Loading catalogue…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {reorder.isError && <InlineError error={reorder.error} onDismiss={() => reorder.reset()} />}
      {(delCat.isError || discontinue.isError) && (
        <InlineError error={delCat.error ?? discontinue.error} onDismiss={() => { delCat.reset(); discontinue.reset(); }} />
      )}

      {data && currentModel && (
        <>
          <FactSentence className="cfgs-facts">
            {currentModel.name} has <Fact>{stats.items} items</Fact> in <Fact>{categories.length} categories</Fact>.{' '}
            {stats.options > 0 && (
              <>
                Of its {stats.options} options,{' '}
                {stats.untied > 0
                  ? <Fact tone="warn" icon={<IconAlertTriangle />} onClick={() => setOnlyUntied((v) => !v)} pressed={onlyUntied} title="Show only the options that write nothing on the order file">{stats.untied} write nothing on the order file</Fact>
                  : <Fact tone="ok">all write on the order file</Fact>}
                {bomCtx ? <> and <Fact tone="info">{stats.bringBom} bring BOM sections in</Fact><FactEnd>.</FactEnd></> : <FactEnd>.</FactEnd>}
              </>
            )}
          </FactSentence>
          <FactSentence sub className="cfgs-facts-sub">
            A confirmed order writes the boat’s Order Sheet row from these ties, then its order file and BOM go to Info / Files{folder ? ` › ${folder}` : ''}.{' '}
            {bomQ.isLoading ? 'Checking the BOM list…'
              : bomMeta ? <>The BOM comes from the {bomMeta.season} list{bomMeta.uploadedAt ? `, uploaded ${df.date(bomMeta.uploadedAt)}` : ''}.</>
              : <>There’s no BOM list for the {bomModel || 'model'} yet, so no BOM is made.</>}
            {canProduction && <> <Link to="/bom-lists">BOM lists</Link> · <Link to="/order-sheet">Order Sheet</Link></>}
          </FactSentence>

          <div className="cfgs-bar">
            <SearchInput value={q} onChange={setQ} placeholder="Search items, order file rows, BOM sections" width={320} />
            {stats.untied > 0 && <FilterToggle checked={onlyUntied} onChange={setOnlyUntied} title="Only the options that write nothing on the order file">Not on the order file ({stats.untied})</FilterToggle>}
            {stats.inactive > 0 && <FilterToggle checked={showInactive} onChange={setShowInactive} title="Discontinued items stay on existing orders but can’t be picked for new ones">Discontinued ({stats.inactive})</FilterToggle>}
          </div>
        </>
      )}

      {data && currentModel && (
        <div className="cfgs-layout">
          <nav className="cfgs-index" aria-label="Categories">
            {categories.map((c) => {
              const shown = filtered.find((x) => x.id === c.id);
              const untied = c.items.filter((it) => it.active && isOption(c, it) && !(linkInfo.get(it.id)?.cells.length)).length;
              return (
                <button key={c.id} type="button" className="cfgs-index-item" disabled={!shown} onClick={() => jump(c.id)} title={c.name}>
                  <span className="cfgs-index-name">{c.name}</span>
                  {untied > 0 && <span className="cfgs-index-warn" title={`${untied} option${untied === 1 ? '' : 's'} write nothing on the order file`}>{untied}</span>}
                  <span className="cfgs-index-n">{shown && narrowed ? `${shown.items.length}/` : ''}{c.items.filter((it) => showInactive || it.active).length}</span>
                </button>
              );
            })}
          </nav>

          <div className="cfgs-main">
            <section className="card cfgs-gallery" aria-label="Model gallery">
              <div className="card-hd">
                <span className="card-title"><IconCamera size={15} /> Model gallery</span>
                <span className="cfgs-sub-note">The photos at the top of the configurator · {galleryPhotos.length} of 6</span>
              </div>
              {(uploadPhoto.isError || deletePhoto.isError) && (
                <InlineError error={uploadPhoto.error ?? deletePhoto.error} onDismiss={() => { uploadPhoto.reset(); deletePhoto.reset(); }} />
              )}
              <PhotoStrip
                photos={galleryPhotos.length ? galleryPhotos : EMPTY_PHOTOS}
                canWrite
                busy={uploadPhoto.isPending}
                onUpload={(file) => galleryRef && uploadPhoto.mutate({ entity: 'catalogue', refKey: galleryRef, file })}
                onDelete={(id) => deletePhoto.mutate({ id, entity: 'catalogue' })}
                deleteMessage={`Delete this photo from the ${currentModel.name} gallery? It leaves the configurator for every user and can’t be restored.`}
              />
            </section>

            {filtered.map((cat) => {
              // `cat` is filtered (search / hidden inactive items); count and guard Delete on the full category.
              const full = categories.find((c) => c.id === cat.id)!;
              const ci = categories.indexOf(full);
              const total = full.items.length;
              const optional = cat.kind !== 'standard';
              return (
                <section key={cat.id} id={`cfgs-cat-${cat.id}`} className="cfgs-cat" aria-labelledby={`cfgs-cat-${cat.id}-t`}>
                  <div className="cfgs-cat-hd">
                    <h2 id={`cfgs-cat-${cat.id}-t`} className="cfgs-cat-t">{cat.name}</h2>
                    <Pill kind={optional ? 'info' : undefined}>{kindLabel(cat.kind)}</Pill>
                    {!cat.discountEligible && <Pill kind="warn" title="Billed at full price: the dealer discount doesn’t apply">No dealer discount</Pill>}
                    <span className="cfgs-cat-n">{cat.items.length}{cat.items.length !== total ? ` of ${total}` : ''} item{total === 1 ? '' : 's'}</span>
                    <span className="cfgs-cat-acts">
                      <button className="btn btn-sm btn-ghost cfgs-ico" title="Move up" aria-label={`Move ${cat.name} up`} disabled={ci === 0 || narrowed || reordering} onClick={() => moveCategory(ci, -1)}><Up /></button>
                      <button className="btn btn-sm btn-ghost cfgs-ico" title="Move down" aria-label={`Move ${cat.name} down`} disabled={ci === categories.length - 1 || narrowed || reordering} onClick={() => moveCategory(ci, 1)}><Down /></button>
                      <button className="btn btn-sm btn-ghost" onClick={() => setCatEdit(full)}>Edit</button>
                      <button className="btn btn-sm btn-ghost" disabled={total > 0 || delCat.isPending} title={total > 0 ? 'Move or discontinue its items first' : 'Delete category'}
                        onClick={async () => { if (await confirm({ title: 'Delete category', message: `Delete category “${cat.name}”?`, confirmLabel: 'Delete', danger: true })) delCat.mutate(cat.id); }}>Delete</button>
                      <button className="btn btn-sm" onClick={() => currentModelId && setItemEdit(itemToForm(currentModelId, cat.id))}><IconPlus size={14} /> Item</button>
                    </span>
                  </div>
                  <div className="tbl-wrap">
                    <table className="tbl tbl-cards cfgs-tbl">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th className="num">Price</th>
                          <th>On the order file</th>
                          {optional && <th>Brings into the BOM</th>}
                          <th><span className="sr-only">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.items.map((it) => {
                          const ii = full.items.indexOf(it);
                          const l = linkInfo.get(it.id)!;
                          const opt = isOption(cat, it);
                          const meta = [
                            it.isStandard && optional ? 'Standard item' : '',
                            it.hasDrawing ? 'Has a drawing' : '',
                            it.assemblyExportLabel ? `Assembly: ${it.assemblyExportLabel}` : '',
                            it.orderUsageCount ? `On ${it.orderUsageCount} order${it.orderUsageCount === 1 ? '' : 's'}` : '',
                          ].filter(Boolean);
                          return (
                            <tr key={it.id} className={it.active ? undefined : 'cfgs-off'}>
                              <td className="tc-title cfgs-item">
                                <span className="cfgs-item-name">
                                  {it.name}
                                  {it.structuredApproved && it.seatingRows != null && <Pill kind="accent">Helm</Pill>}
                                  {!it.active && <Pill kind="warn">Discontinued</Pill>}
                                </span>
                                {meta.length > 0 && <span className="cfgs-item-meta">{meta.join(' · ')}</span>}
                              </td>
                              <td className="num cfgs-price tc-meta">
                                {it.customerPrice != null ? <span>{money(it.customerPrice)}</span> : <span className="cfgs-none">{optional ? 'No price' : 'Included'}</span>}
                                {it.dealerPrice != null && <span className="cfgs-item-meta">Dealer {money(it.dealerPrice)}</span>}
                              </td>
                              <td className="tc-meta cfgs-ties-cell">
                                {l.cells.length ? <TieChips cells={l.cells} from={l.from} />
                                  : opt ? <Pill kind="warn">Writes nothing</Pill>
                                  : <span className="cfgs-none">Nothing</span>}
                              </td>
                              {optional && (
                                <td className={`tc-meta cfgs-bom${opt ? '' : ' tc-hide'}`}>
                                  {!opt ? '' : l.bom == null ? <span className="cfgs-none">No BOM list</span> : l.bom.length ? <span title={l.bom.join(', ')}>{l.bom.slice(0, 2).join(', ')}{l.bom.length > 2 && <span className="cfgs-none"> +{l.bom.length - 2}</span>}</span> : <span className="cfgs-none">Nothing extra</span>}
                                </td>
                              )}
                              <td className="tc-actions cfgs-acts">
                                <button className="btn btn-sm btn-ghost cfgs-ico" title="Move up" aria-label={`Move ${it.name} up`} disabled={ii === 0 || narrowed || reordering} onClick={() => moveItem(full, ii, -1)}><Up /></button>
                                <button className="btn btn-sm btn-ghost cfgs-ico" title="Move down" aria-label={`Move ${it.name} down`} disabled={ii === full.items.length - 1 || narrowed || reordering} onClick={() => moveItem(full, ii, 1)}><Down /></button>
                                <button className="btn btn-sm" onClick={() => currentModelId && setItemEdit(itemToForm(currentModelId, it.categoryId, it))}>Edit</button>
                                {it.active && (
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    disabled={discontinue.isPending}
                                    onClick={async () => {
                                      if (await confirm({ title: 'Discontinue item', message: `Discontinue “${it.name}”? It stays on existing orders but can’t be picked for new ones.`, confirmLabel: 'Discontinue', danger: true })) discontinue.mutate(it.id);
                                    }}
                                  >Discontinue</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {cat.items.length === 0 && (
                          <tr><td colSpan={optional ? 5 : 4} className="cfgs-empty">{total > 0 ? `No items shown: ${total} hidden by the filters.` : 'No items yet. Add one with Item.'}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
            {categories.length > 0 && filtered.length === 0 && <EmptyState title="Nothing matches" hint="Try other words, or clear the filters." />}
            {categories.length === 0 && <EmptyState title="No categories yet" hint="Add the first one with Category." />}
          </div>
        </div>
      )}

      {itemEdit && <ItemModal initial={itemEdit} categories={categories} canEditAssembly={canEditAssembly} bom={bomCtx} bomFrom={bomFrom} onClose={() => setItemEdit(null)} />}
      {catEdit !== undefined && currentModelId && <CategoryModal modelId={currentModelId} cat={catEdit} onClose={() => setCatEdit(undefined)} />}
      {modelsOpen && <ModelsModal models={data?.models ?? []} onClose={() => setModelsOpen(false)} />}
      {confirmNode}
    </>
  );
}

